import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import ThemeToggle from "../components/ThemeToggle";
import {
  settingsService,
  type SiteSettings,
} from "../../../services/settings.service";

type SettingsTab = "general" | "contact" | "appearance";

const tabs: { id: SettingsTab; label: string; icon: string }[] = [
  { id: "general", label: "כללי", icon: "ri-settings-3-line" },
  { id: "contact", label: "פרטי קשר", icon: "ri-contacts-line" },
  { id: "appearance", label: "מראה ותצוגה", icon: "ri-palette-line" },
];

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-rose-100 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-rose-50 dark:border-slate-700">
        <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-rose-50 dark:bg-slate-700 text-rose-400">
          <i className={`${icon} text-base`}></i>
        </div>
        <h3 className="font-bold text-gray-800 dark:text-slate-200 text-sm">
          {title}
        </h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b-2 border-rose-50 dark:border-slate-700 last:border-0">
      <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 sm:w-40 flex-shrink-0">
        {label}
      </span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative w-10 h-5 rounded-full border-2 transition-all cursor-pointer flex-shrink-0 ${checked ? "bg-rose-400 border-rose-400" : "bg-gray-200 border-gray-200"}`}
    >
      <span
        className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${checked ? "right-0.5" : "left-0.5"}`}
      />
    </button>
  );
}

const inputClass =
  "w-full px-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 dark:border-slate-600 bg-rose-50/30 dark:bg-slate-700 dark:text-slate-200 focus:outline-none focus:border-rose-300 dark:focus:border-rose-500 transition-all";

export default function SettingsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const [settings, setSettings] = useState<SiteSettings>({
    site_name: "",
    site_email: "",
    site_phone: "",
    site_address: "",
    facebook_url: "",
    instagram_url: "",
    linkedin_url: "",
    about_text: "",
    registration_open: "1",
    show_partners: "1",
    maintenance_mode: "0",
  });

  useEffect(() => {
    settingsService.get().then((data) => {
      setSettings(data);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    await settingsService.update(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const set = (key: keyof SiteSettings, value: string) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <AdminLayout title="הגדרות" subtitle="ניהול הגדרות המערכת">
      <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border-2 border-rose-100 dark:border-slate-700 rounded-2xl p-1.5 mb-6 w-fit flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-rose-400 text-white"
                : "text-gray-500 dark:text-slate-400 hover:bg-rose-50 dark:hover:bg-slate-700"
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${tab.icon} text-sm`}></i>
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {saved && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-emerald-600 text-sm font-semibold">
          <i className="ri-checkbox-circle-line text-base"></i>
          ההגדרות נשמרו בהצלחה!
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <p className="text-gray-500 dark:text-slate-400">טוען...</p>
        </div>
      ) : (
        <>
          {/* General Tab */}
          {activeTab === "general" && (
            <div className="space-y-5 max-w-2xl">
              <SectionCard title="פרטי הפלטפורמה" icon="ri-global-line">
                <FieldRow label="שם האתר">
                  <input
                    type="text"
                    value={settings.site_name}
                    onChange={(e) => set("site_name", e.target.value)}
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="אודות">
                  <textarea
                    rows={3}
                    value={settings.about_text}
                    onChange={(e) => set("about_text", e.target.value)}
                    placeholder="טקסט קצר על הפלטפורמה..."
                    className={`${inputClass} resize-none`}
                  />
                </FieldRow>
              </SectionCard>

              <SectionCard title="הגדרות תצוגה ופעולה" icon="ri-toggle-line">
                <FieldRow label="הרשמה פתוחה">
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      checked={settings.registration_open === "1"}
                      onChange={() =>
                        set(
                          "registration_open",
                          settings.registration_open === "1" ? "0" : "1",
                        )
                      }
                    />
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      {settings.registration_open === "1"
                        ? "הרשמה פעילה לכל הפעילויות"
                        : "הרשמה סגורה"}
                    </span>
                  </div>
                </FieldRow>
                <FieldRow label="הצג שותפים">
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      checked={settings.show_partners === "1"}
                      onChange={() =>
                        set(
                          "show_partners",
                          settings.show_partners === "1" ? "0" : "1",
                        )
                      }
                    />
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      {settings.show_partners === "1"
                        ? "לוגואי שותפים מוצגים באתר"
                        : "לוגואים מוסתרים"}
                    </span>
                  </div>
                </FieldRow>
              </SectionCard>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-save-line text-base"></i>
                  שמור שינויים
                </button>
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === "contact" && (
            <div className="space-y-5 max-w-2xl">
              <SectionCard title="פרטי קשר" icon="ri-contacts-line">
                <FieldRow label="אימייל">
                  <input
                    type="email"
                    value={settings.site_email}
                    onChange={(e) => set("site_email", e.target.value)}
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="טלפון">
                  <input
                    type="text"
                    value={settings.site_phone}
                    onChange={(e) => set("site_phone", e.target.value)}
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="כתובת">
                  <input
                    type="text"
                    value={settings.site_address}
                    onChange={(e) => set("site_address", e.target.value)}
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="פייסבוק">
                  <input
                    type="text"
                    value={settings.facebook_url}
                    onChange={(e) => set("facebook_url", e.target.value)}
                    placeholder="https://facebook.com/..."
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="אינסטגרם">
                  <input
                    type="text"
                    value={settings.instagram_url}
                    onChange={(e) => set("instagram_url", e.target.value)}
                    placeholder="https://instagram.com/..."
                    className={inputClass}
                  />
                </FieldRow>
                <FieldRow label="קישור ווייז">
                  <input
                    type="text"
                    value={settings.linkedin_url}
                    onChange={(e) => set("linkedin_url", e.target.value)}
                    placeholder="https://www.waze.com/..."
                    className={inputClass}
                  />
                </FieldRow>
              </SectionCard>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-save-line text-base"></i>
                  שמור שינויים
                </button>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === "appearance" && (
            <div className="space-y-5 max-w-2xl">
              <SectionCard title="מראה" icon="ri-palette-line">
                <FieldRow label="תצוגה כהה">
                  <ThemeToggle />
                </FieldRow>
              </SectionCard>

              <SectionCard title="מצב תחזוקה" icon="ri-tools-line">
                <FieldRow label="הפעל מצב תחזוקה">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <ToggleSwitch
                        checked={settings.maintenance_mode === "1"}
                        onChange={() =>
                          set(
                            "maintenance_mode",
                            settings.maintenance_mode === "1" ? "0" : "1",
                          )
                        }
                      />
                      <span
                        className={`text-xs font-semibold ${settings.maintenance_mode === "1" ? "text-amber-500" : "text-gray-500 dark:text-slate-400"}`}
                      >
                        {settings.maintenance_mode === "1"
                          ? "⚠️ האתר במצב תחזוקה"
                          : "האתר פעיל"}
                      </span>
                    </div>
                    {settings.maintenance_mode === "1" && (
                      <div className="flex items-center gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></div>
                        <div>
                          <p className="text-xs font-semibold text-amber-600">
                            מצב תחזוקה פעיל
                          </p>
                          <p className="text-xs text-amber-500 mt-1">
                            מבקרים יופנו לדף תחזוקה. נהל את הדף בהגדרות.
                          </p>
                        </div>
                        <button
                          onClick={() => navigate("/admin/maintenance")}
                          className="mr-auto px-4 py-2 rounded-xl bg-amber-400 text-white text-xs font-bold hover:bg-amber-500 transition-all cursor-pointer whitespace-nowrap"
                        >
                          צפה בתצוגה
                        </button>
                      </div>
                    )}
                  </div>
                </FieldRow>
              </SectionCard>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-save-line text-base"></i>
                  שמור שינויים
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
